// LeadCreateForm — the field stack used by the kanban "+" entry point and
// the customer-detail "Add lead" flow.
//
// Layout intent (see design at /Users/datnguyen/workspace/playground/Lead
// create.html): a wide modal with a two-column body.
//
//   Left column (1.4fr — the main form):
//     1. Khách hàng        — customer phone-lookup (existing CustomerPhoneLookup)
//     2. Nguồn khách       — emoji-chip row, one chip per CustomerSource
//     3. Loại giao dịch    — Mua / Thuê pill toggle (maps to desiredListingType)
//     4. Khu vực quan tâm  — searchable WardCombobox
//     5. Ngân sách         — dual-handle range slider with mono price readout
//
//   Right column (1fr — pipeline metadata, separated by a left border):
//     1. BĐS QUAN TÂM      — property picker (reuses PropertyPickerField)
//     2. PHÂN CÔNG         — broker picker (Popover + Command)
//     3. FOLLOW-UP TIẾP THEO — quick-pick select (Sau 1 ngày / 2 ngày / 1 tuần / Tuỳ chỉnh…)
//
// Footer: small status hint on the left + "Huỷ" / "Tạo lead" on the right.
//
// The component stays a single render — no Dialog wrapper. The kanban's
// `LeadCreateSurface` and any future host owns the modal chrome (so the
// customer-locked variant on the customer-detail page can host the same
// form inline if needed). The `mode` prop's two variants are preserved:
//   - "with-phone-lookup": the default kanban entry; the customer block
//     at the top renders the phone-lookup widget. The user types a
//     phone, the form pins or creates the customer accordingly.
//   - "customer-locked": entered from the customer-detail page; the
//     customer is pinned at mount and the phone-lookup widget is hidden.
//
// Backend contract: every field collected here is already on
// LeadCreateRequest (interestedPropertyId, desiredListingType,
// desiredWard, budgetMin, budgetMax, nextFollowUpAt, source on the
// customer side).

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import {
  ArrowRight,
  Briefcase,
  Calendar,
  Check,
  ChevronDown,
  Loader2,
  Plus,
  RefreshCw,
  User as UserIcon,
  X,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Slider } from "@/components/ui/slider";
import { ApiError } from "@/lib/api-client";
import { localizeError } from "@/lib/errorMessages";
import { applyServerValidationErrors } from "@/lib/form-errors";
import { useCreateLeadFlow } from "@/features/lead/api";
import { useBrokers } from "@/features/appointment/hooks/use-brokers";
import { useAuthStore } from "@/lib/auth-store";
import { seesAllLeads } from "@/features/settings/role-labels";
import { useProperties } from "@/features/property/api";
import { WardCombobox } from "@/components/ward-combobox";
import { formatPrice } from "@/features/property/utils/format-price";
import { CUSTOMER_SOURCE_LABELS } from "@/features/lead/constants";
import {
  CustomerPhoneLookup,
  type CustomerLookupState,
} from "@/features/lead/components/customer-phone-lookup";
import type { ListingType } from "@/types/property";
import type {
  Customer,
  CustomerSource,
  Lead,
  LeadStatus,
} from "@/features/lead/types";

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

const VN_PHONE_REGEX = /^(\+84|0)\d{9,10}$/;

const customerSubSchema = z.object({
  phoneNumber: z
    .string()
    .min(1, "Vui lòng nhập số điện thoại")
    .refine(
      (v) => VN_PHONE_REGEX.test(v.replace(/\s+/g, "")),
      "Số điện thoại không hợp lệ",
    ),
  fullName: z
    .string()
    .max(200, "Họ và tên tối đa 200 ký tự")
    .optional()
    .or(z.literal("")),
  email: z.string().email("Email không hợp lệ").optional().or(z.literal("")),
  source: z.enum(["WALK_IN", "FACEBOOK", "ZALO", "REFERRAL", "OTHER"]),
});

const leadSubSchema = z.object({
  status: z.enum([
    "NEW",
    "CONTACTED",
    "VIEWING_SCHEDULED",
    "NEGOTIATING",
    "WON",
    "LOST",
  ]),
  assignedBrokerId: z.string().optional(),
  interestedPropertyId: z.string().optional(),
  desiredListingType: z.enum(["SALE", "RENT"]).optional(),
  desiredWard: z.string().optional(),
  budgetMin: z.number().min(0).optional(),
  budgetMax: z.number().min(0).optional(),
  nextFollowUpAt: z.string().optional(),
});

const phoneLookupSchema = customerSubSchema.merge(leadSubSchema);
const customerLockedSchema = leadSubSchema;

type PhoneLookupValues = z.infer<typeof phoneLookupSchema>;
type CustomerLockedValues = z.infer<typeof customerLockedSchema>;
type LeadCreateValues = PhoneLookupValues | CustomerLockedValues;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export type LeadCreateFormMode =
  | { kind: "with-phone-lookup"; initialStatus: LeadStatus }
  | { kind: "customer-locked"; prefilledCustomer: Customer };

export interface LeadCreateFormProps {
  mode: LeadCreateFormMode;
  onSuccess: (lead: Lead) => void;
  onCancel: () => void;
}

function isPhoneLookupValues(
  _values: LeadCreateValues,
  mode: LeadCreateFormMode["kind"],
): _values is PhoneLookupValues {
  return mode === "with-phone-lookup";
}

// ---------------------------------------------------------------------------
// Budget slider config
//
// The slider operates in raw VND so the numeric output matches the
// backend `budgetMin` / `budgetMax` DTO fields exactly. We render the
// readout in `tỷ` for SALE and `triệu` for RENT, and switch the slider
// max + step so the granularity matches the listing type.
// ---------------------------------------------------------------------------

const ONE_BILLION = 1_000_000_000;
const ONE_MILLION = 1_000_000;

interface BudgetScale {
  min: number;
  max: number;
  step: number;
  unit: "ty" | "trieu";
  /** Tick labels rendered under the slider. */
  ticks: readonly string[];
}

const BUDGET_SCALE_SALE: BudgetScale = {
  min: 0,
  // 20 billion VND. The readout shows "20+" so the upper handle parked
  // at max signals "no upper limit" to the broker.
  max: 20 * ONE_BILLION,
  step: 100 * ONE_MILLION, // 0.1 tỷ
  unit: "ty",
  ticks: ["0", "5 tỷ", "10", "15", "20+"],
};

const BUDGET_SCALE_RENT: BudgetScale = {
  min: 0,
  // 100 triệu/tháng — generous upper bound for Hanoi rentals.
  max: 100 * ONE_MILLION,
  step: ONE_MILLION, // 1 triệu
  unit: "trieu",
  ticks: ["0", "25 tr", "50", "75", "100+"],
};

function formatBudget(value: number, unit: "ty" | "trieu"): string {
  if (unit === "ty") {
    const tỷ = value / ONE_BILLION;
    const fixed = tỷ.toFixed(1);
    return fixed.endsWith(".0") ? fixed.slice(0, -2) : fixed;
  }
  const tr = value / ONE_MILLION;
  return tr.toFixed(0);
}

// ---------------------------------------------------------------------------
// Source chip metadata. The emoji + Vietnamese label combination matches
// the design comp. Order mirrors the visual prominence in the screenshot
// (Facebook + Zalo first since those are the dominant channels for Hanoi
// brokerages).
// ---------------------------------------------------------------------------

const SOURCE_EMOJI: Record<CustomerSource, string> = {
  FACEBOOK: "📘",
  ZALO: "💬",
  REFERRAL: "🤝",
  WALK_IN: "🚶",
  OTHER: "✦",
};

const SOURCE_DISPLAY_ORDER: readonly CustomerSource[] = [
  "FACEBOOK",
  "ZALO",
  "REFERRAL",
  "WALK_IN",
  "OTHER",
];

// ---------------------------------------------------------------------------
// Follow-up quick picks. The string `value` is parsed at submit time into
// an ISO datetime relative to "now". Custom value falls through to the
// native datetime-local input.
// ---------------------------------------------------------------------------

type FollowUpPick = "1d" | "2d" | "1w" | "custom";

const FOLLOW_UP_LABELS: Record<FollowUpPick, string> = {
  "1d": "Sau 1 ngày",
  "2d": "Sau 2 ngày",
  "1w": "Sau 1 tuần",
  custom: "Tuỳ chỉnh…",
};

function followUpPickToIso(pick: FollowUpPick, now: Date): string | null {
  if (pick === "custom") return null;
  const next = new Date(now);
  if (pick === "1d") next.setDate(next.getDate() + 1);
  else if (pick === "2d") next.setDate(next.getDate() + 2);
  else next.setDate(next.getDate() + 7);
  return next.toISOString();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LeadCreateForm({
  mode,
  onSuccess,
  onCancel,
}: LeadCreateFormProps) {
  const mutation = useCreateLeadFlow();
  const brokersQuery = useBrokers();
  // OWNER/MANAGER may assign a lead to any broker; an agent always owns what
  // they create (the backend enforces this), so the picker is hidden for them.
  const canAssignBroker = seesAllLeads(useAuthStore((s) => s.user)?.role);
  // Wrap the `data ?? []` defaults in useMemo so downstream useMemo
  // calls (`selectedProperty`, `selectedBroker`) get a stable array
  // identity across renders. Without this React Compiler complains
  // that the array identity changes every render.
  const brokers = useMemo(
    () => brokersQuery.data ?? [],
    [brokersQuery.data],
  );
  // Properties list for the interested-property picker. Same page size
  // as the appointment form (50) since the picker is a quick search,
  // not a paginator.
  const propertiesQuery = useProperties({ page: 0, size: 50 });
  const properties = useMemo(
    () => propertiesQuery.data?.items ?? [],
    [propertiesQuery.data],
  );

  // Customer state-of-truth lives outside RHF so the lookup widget can
  // pin it as the phone field changes.
  const initialCustomerState = useMemo<
    | { kind: "existing"; customer: Customer }
    | { kind: "new" }
  >(() => {
    if (mode.kind === "customer-locked") {
      return { kind: "existing", customer: mode.prefilledCustomer };
    }
    return { kind: "new" };
  }, [mode]);

  const [customerState, setCustomerState] = useState(initialCustomerState);

  // Budget slider state — kept locally as a tuple so dragging stays fluid.
  // We mirror to the form on `onValueCommit` so RHF only sees the final
  // value (not every intermediate tick).
  const [budget, setBudget] = useState<[number, number]>([0, 20 * ONE_BILLION]);

  // Quick-pick follow-up state. Default "Sau 2 ngày" to match the design.
  const [followUpPick, setFollowUpPick] = useState<FollowUpPick>("2d");
  // Stable "now" captured at mount so we don't compute Date.now() in render.
  const [nowAtMount] = useState<() => Date>(() => () => new Date());

  // Broker picker state.
  const [brokerPickerOpen, setBrokerPickerOpen] = useState(false);
  // Interested-property picker state.
  const [propertyPickerOpen, setPropertyPickerOpen] = useState(false);

  const schema =
    mode.kind === "with-phone-lookup" ? phoneLookupSchema : customerLockedSchema;

  const form = useForm<LeadCreateValues>({
    resolver: zodResolver(schema),
    defaultValues:
      mode.kind === "with-phone-lookup"
        ? {
            phoneNumber: "",
            fullName: "",
            email: "",
            source: "FACEBOOK" as CustomerSource,
            status: mode.initialStatus,
            assignedBrokerId: "",
            interestedPropertyId: "",
            desiredListingType: "SALE" as ListingType,
            desiredWard: "",
            nextFollowUpAt: "",
          }
        : {
            status: "NEW" as LeadStatus,
            assignedBrokerId: "",
            interestedPropertyId: "",
            desiredListingType: "SALE" as ListingType,
            desiredWard: "",
            nextFollowUpAt: "",
          },
  });
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
    setError,
    clearErrors,
  } = form;

  const source =
    mode.kind === "with-phone-lookup"
      ? watch("source" as keyof LeadCreateValues)
      : "FACEBOOK";
  const desiredListingType = watch(
    "desiredListingType" as keyof LeadCreateValues,
  ) as ListingType | undefined;
  const desiredWardValue = watch(
    "desiredWard" as keyof LeadCreateValues,
  ) as string | undefined;
  const interestedPropertyIdValue = watch(
    "interestedPropertyId" as keyof LeadCreateValues,
  ) as string | undefined;
  const brokerIdValue = watch("assignedBrokerId") as string | undefined;
  const customNextFollowUp = watch(
    "nextFollowUpAt" as keyof LeadCreateValues,
  ) as string | undefined;
  const phoneValue =
    mode.kind === "with-phone-lookup"
      ? (watch("phoneNumber" as keyof LeadCreateValues) as
          | string
          | undefined) ?? ""
      : "";

  const budgetScale: BudgetScale =
    desiredListingType === "RENT" ? BUDGET_SCALE_RENT : BUDGET_SCALE_SALE;

  // When the user switches between SALE and RENT, snap the upper handle
  // back to the new scale's max so we don't carry over a 20-tỷ value
  // into a 100-triệu RENT slider.
  useEffect(() => {
    setBudget(([lo, hi]) => {
      const clampedHi = Math.min(hi, budgetScale.max);
      const clampedLo = Math.min(lo, clampedHi);
      // Only reset when the previous value was outside the new range.
      if (clampedHi !== hi || clampedLo !== lo) {
        return [clampedLo, clampedHi];
      }
      return [lo, hi];
    });
  }, [budgetScale.max]);

  const handleLookupStateChange = useCallback(
    (lookup: CustomerLookupState) => {
      if (lookup.kind === "match") {
        setCustomerState({ kind: "existing", customer: lookup.customer });
      } else {
        setCustomerState({ kind: "new" });
      }
    },
    [],
  );

  // Mirror matched-customer fields into the form so submit sees them.
  useEffect(() => {
    if (mode.kind !== "with-phone-lookup") return;
    if (customerState.kind === "existing") {
      setValue("fullName" as keyof LeadCreateValues, customerState.customer.fullName);
      setValue("email" as keyof LeadCreateValues, customerState.customer.email ?? "");
      setValue("source" as keyof LeadCreateValues, customerState.customer.source);
    }
  }, [customerState, mode.kind, setValue]);

  const selectedProperty = useMemo(() => {
    if (
      interestedPropertyIdValue === undefined ||
      interestedPropertyIdValue === ""
    )
      return null;
    const id = Number.parseInt(interestedPropertyIdValue, 10);
    if (Number.isNaN(id)) return null;
    return properties.find((p) => p.id === id) ?? null;
  }, [interestedPropertyIdValue, properties]);

  const selectedBroker = useMemo(() => {
    if (brokerIdValue === undefined || brokerIdValue === "") return null;
    const id = Number.parseInt(brokerIdValue, 10);
    if (Number.isNaN(id)) return null;
    return brokers.find((b) => b.id === id) ?? null;
  }, [brokerIdValue, brokers]);

  // Resolve the effective `nextFollowUpAt` ISO string to submit.
  const resolveNextFollowUp = (): string | undefined => {
    if (followUpPick === "custom") {
      // The native datetime-local input emits `YYYY-MM-DDTHH:mm`. We
      // re-parse into a Date so we can ship a full ISO with TZ.
      if (
        customNextFollowUp === undefined ||
        customNextFollowUp === null ||
        customNextFollowUp === ""
      )
        return undefined;
      const dt = new Date(customNextFollowUp);
      return Number.isNaN(dt.getTime()) ? undefined : dt.toISOString();
    }
    return followUpPickToIso(followUpPick, nowAtMount()) ?? undefined;
  };

  const onSubmit = async (values: LeadCreateValues) => {
    clearErrors();

    const lookupValues = isPhoneLookupValues(values, mode.kind) ? values : null;

    // Lead-fields half (shared between both modes). Only include fields
    // the user has actually populated so the backend DTO stays clean.
    const interestedPropertyId =
      values.interestedPropertyId !== undefined &&
      values.interestedPropertyId !== ""
        ? Number.parseInt(values.interestedPropertyId, 10)
        : undefined;
    const assignedBrokerId =
      values.assignedBrokerId !== undefined && values.assignedBrokerId !== ""
        ? Number.parseInt(values.assignedBrokerId, 10)
        : undefined;
    const desiredWard =
      values.desiredWard !== undefined && values.desiredWard !== ""
        ? values.desiredWard
        : undefined;
    const desiredListingTypeValue = values.desiredListingType;
    const [budgetMin, budgetMax] = budget;
    const nextFollowUpIso = resolveNextFollowUp();

    const baseLeadFields = {
      ...(values.status !== undefined ? { status: values.status } : {}),
      ...(assignedBrokerId !== undefined ? { assignedBrokerId } : {}),
      ...(interestedPropertyId !== undefined ? { interestedPropertyId } : {}),
      ...(desiredListingTypeValue !== undefined
        ? { desiredListingType: desiredListingTypeValue }
        : {}),
      ...(desiredWard !== undefined ? { desiredWard } : {}),
      // Only send non-zero budgets so leads with no stated budget keep
      // null on the backend. budgetMin === budgetMax === 0 is the "no
      // budget set" default.
      ...(budgetMin > 0 ? { budgetMin } : {}),
      ...(budgetMax > 0 && budgetMax < budgetScale.max
        ? { budgetMax }
        : {}),
      ...(nextFollowUpIso !== undefined
        ? { nextFollowUpAt: nextFollowUpIso }
        : {}),
    };

    let existingCustomerId: number | null;
    let newCustomer: Parameters<typeof mutation.mutateAsync>[0]["newCustomer"];
    if (customerState.kind === "existing") {
      existingCustomerId = customerState.customer.id;
      newCustomer = null;
    } else if (lookupValues !== null) {
      if (!lookupValues.fullName || lookupValues.fullName.trim().length === 0) {
        setError("fullName" as keyof LeadCreateValues, {
          type: "required",
          message: "Vui lòng nhập họ tên",
        });
        return;
      }
      existingCustomerId = null;
      newCustomer = {
        fullName: lookupValues.fullName.trim(),
        phoneNumber: lookupValues.phoneNumber.replace(/\s+/g, ""),
        source: lookupValues.source,
        ...(lookupValues.email && lookupValues.email.length > 0
          ? { email: lookupValues.email }
          : {}),
      };
    } else {
      toast.error(localizeError(new Error("Missing customer")));
      return;
    }

    try {
      const lead = await mutation.mutateAsync({
        existingCustomerId,
        newCustomer,
        leadFields: baseLeadFields,
      });
      toast.success("Đã tạo khách hàng tiềm năng");
      onSuccess(lead);
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.body?.code === "CUSTOMER_PHONE_DUPLICATE") {
          setError("phoneNumber" as keyof LeadCreateValues, {
            type: "duplicate",
            message: "Số điện thoại đã được dùng. Tải lại trang.",
          });
          return;
        }
        const handled = applyServerValidationErrors(form, error);
        if (handled) return;
      }
      toast.error(localizeError(error));
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="flex flex-col"
      aria-label="Thêm khách hàng tiềm năng"
    >
      <div className="grid grid-cols-1 gap-0 md:grid-cols-[1.4fr_1fr]">
        {/* ============================================================== */}
        {/* Left column — main form                                        */}
        {/* ============================================================== */}
        <div className="flex flex-col gap-5 px-5 py-4 md:py-5">
          {/* Section 1 — Customer */}
          {mode.kind === "with-phone-lookup" ? (
            <CustomerSection
              phoneValue={phoneValue}
              customerState={customerState}
              onLookupStateChange={handleLookupStateChange}
              register={register}
              errors={errors}
              setValue={setValue}
              clearErrors={clearErrors}
            />
          ) : (
            <LockedCustomerCard customer={mode.prefilledCustomer} />
          )}

          {/* Section 2 — Nguồn khách (only renders in phone-lookup mode) */}
          {mode.kind === "with-phone-lookup" ? (
            <section>
              <p className="mb-2 text-[11px] font-semibold text-ink-soft">
                Nguồn khách
              </p>
              <div className="flex flex-wrap gap-2">
                {SOURCE_DISPLAY_ORDER.map((s) => {
                  const isActive = source === s;
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() =>
                        setValue(
                          "source" as keyof LeadCreateValues,
                          s,
                          { shouldDirty: true },
                        )
                      }
                      disabled={customerState.kind === "existing"}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-medium transition-colors",
                        "disabled:cursor-not-allowed disabled:opacity-60",
                        isActive
                          ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                          : "border-border bg-card text-ink-soft hover:bg-muted",
                      )}
                      aria-pressed={isActive}
                    >
                      <span aria-hidden="true">{SOURCE_EMOJI[s]}</span>
                      {CUSTOMER_SOURCE_LABELS[s]}
                    </button>
                  );
                })}
              </div>
            </section>
          ) : null}

          {/* Section 3 — Loại giao dịch (Mua / Thuê toggle) */}
          <section>
            <p className="mb-2 text-[11px] font-semibold text-ink-soft">
              Loại giao dịch quan tâm
            </p>
            <div className="flex gap-2">
              <ListingTypePill
                value="SALE"
                label="Mua"
                active={desiredListingType === "SALE"}
                onClick={() =>
                  setValue(
                    "desiredListingType" as keyof LeadCreateValues,
                    "SALE" as ListingType,
                    { shouldDirty: true },
                  )
                }
              />
              <ListingTypePill
                value="RENT"
                label="Thuê"
                active={desiredListingType === "RENT"}
                onClick={() =>
                  setValue(
                    "desiredListingType" as keyof LeadCreateValues,
                    "RENT" as ListingType,
                    { shouldDirty: true },
                  )
                }
              />
            </div>
          </section>

          {/* Section 4 — Khu vực quan tâm */}
          <section>
            <Label
              htmlFor="desiredWard"
              className="mb-2 block text-[11px] font-semibold text-ink-soft"
            >
              Khu vực quan tâm
            </Label>
            <WardCombobox
              id="desiredWard"
              value={desiredWardValue === "" ? null : desiredWardValue ?? null}
              onChange={(v) =>
                setValue(
                  "desiredWard" as keyof LeadCreateValues,
                  v ?? "",
                  { shouldDirty: true },
                )
              }
              clearable
              clearLabel="Không chọn"
            />
          </section>

          {/* Section 5 — Ngân sách */}
          <section>
            <div className="mb-2 flex items-baseline justify-between">
              <p className="text-[11px] font-semibold text-ink-soft">
                Ngân sách
              </p>
              <p className="font-mono text-[12px] text-ink-faint tabular-nums">
                {formatBudgetReadout(budget, budgetScale)}
              </p>
            </div>
            <div className="px-1">
              <Slider
                aria-label="Khoảng ngân sách"
                value={budget}
                min={budgetScale.min}
                max={budgetScale.max}
                step={budgetScale.step}
                onValueChange={(v) => {
                  if (v.length === 2) setBudget([v[0]!, v[1]!]);
                }}
              />
            </div>
            <div className="mt-2 flex justify-between px-1 font-mono text-[11px] text-ink-faint">
              {budgetScale.ticks.map((t) => (
                <span key={t}>{t}</span>
              ))}
            </div>
          </section>
        </div>

        {/* ============================================================== */}
        {/* Right column — pipeline metadata                               */}
        {/* ============================================================== */}
        <aside className="flex flex-col gap-5 border-t border-border bg-muted/30 px-5 py-4 md:border-l md:border-t-0 md:py-5">
          {/* BĐS QUAN TÂM */}
          <section>
            <p className="mb-2 font-mono text-[11px] font-semibold tracking-[0.06em] text-ink-faint uppercase">
              BĐS quan tâm
            </p>
            {selectedProperty !== null ? (
              <div className="flex items-center gap-2 rounded-[10px] border border-border bg-card p-2.5">
                <div className="flex size-9 shrink-0 items-center justify-center rounded-[7px] bg-gradient-to-br from-slate-200 to-slate-300 text-slate-500">
                  <Briefcase aria-hidden="true" className="size-4" />
                </div>
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-[14px] font-semibold leading-tight">
                    {selectedProperty.buildingName ??
                      `BĐS #${selectedProperty.id}`}
                  </span>
                  <span className="truncate font-mono text-[11px] text-muted-foreground">
                    {[
                      selectedProperty.internalCode,
                      formatPrice(
                        selectedProperty.price,
                        selectedProperty.listingType,
                      ),
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setValue(
                      "interestedPropertyId" as keyof LeadCreateValues,
                      "",
                      { shouldDirty: true },
                    )
                  }
                  aria-label="Bỏ chọn bất động sản"
                  className="flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
                >
                  <X aria-hidden="true" className="size-3.5" />
                </button>
              </div>
            ) : (
              // modal: this Popover lives inside the create-lead Dialog, whose
              // scroll-lock (react-remove-scroll) otherwise cancels wheel events
              // on the body-portaled list — making a long list unscrollable. A
              // modal Popover owns its own scroll area.
              <Popover
                modal
                open={propertyPickerOpen}
                onOpenChange={setPropertyPickerOpen}
              >
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    disabled={propertiesQuery.isPending}
                    className="flex w-full items-center gap-2 rounded-[10px] border border-dashed border-border bg-card px-3 py-2.5 text-left text-[12px] font-medium text-ink-soft transition-colors hover:bg-muted/40 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Plus
                      aria-hidden="true"
                      className="size-3.5 text-muted-foreground"
                    />
                    Thêm BĐS quan tâm
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="w-[var(--radix-popover-trigger-width)] min-w-[280px] p-0"
                >
                  <Command
                    filter={(itemValue, search) =>
                      itemValue.includes(search.toLowerCase()) ? 1 : 0
                    }
                  >
                    <CommandInput placeholder="Tìm theo tên, mã, khu vực..." />
                    <CommandList>
                      <CommandEmpty>
                        Không có bất động sản phù hợp.
                      </CommandEmpty>
                      <CommandGroup>
                        {properties.map((property) => {
                          const haystack = [
                            property.buildingName,
                            property.internalCode,
                            property.wardDisplayName,
                          ]
                            .filter((s): s is string => Boolean(s))
                            .join(" ")
                            .toLowerCase();
                          return (
                            <CommandItem
                              key={property.id}
                              value={haystack || String(property.id)}
                              onSelect={() => {
                                setValue(
                                  "interestedPropertyId" as keyof LeadCreateValues,
                                  String(property.id),
                                  { shouldDirty: true },
                                );
                                setPropertyPickerOpen(false);
                              }}
                            >
                              <div className="flex w-full items-center gap-2.5">
                                <div className="flex size-8 shrink-0 items-center justify-center rounded-[6px] bg-gradient-to-br from-slate-200 to-slate-300 text-slate-500">
                                  <Briefcase
                                    aria-hidden="true"
                                    className="size-3.5"
                                  />
                                </div>
                                <div className="flex min-w-0 flex-1 flex-col">
                                  <span className="truncate text-[12px] font-semibold leading-tight">
                                    {property.buildingName ??
                                      `BĐS #${property.id}`}
                                  </span>
                                  <span className="truncate font-mono text-[11px] text-muted-foreground">
                                    {[
                                      property.internalCode,
                                      formatPrice(
                                        property.price,
                                        property.listingType,
                                      ),
                                    ]
                                      .filter(Boolean)
                                      .join(" · ")}
                                  </span>
                                </div>
                              </div>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}
          </section>

          {/* PHÂN CÔNG — only OWNER/MANAGER may assign a lead to a broker; an
              agent always owns what they create (enforced server-side), so the
              picker is hidden for them. */}
          {canAssignBroker ? (
          <section>
            <p className="mb-2 font-mono text-[11px] font-semibold tracking-[0.06em] text-ink-faint uppercase">
              Phân công
            </p>
            {/* modal: scrollable inside the Dialog — see the property picker above. */}
            <Popover
              modal
              open={brokerPickerOpen}
              onOpenChange={setBrokerPickerOpen}
            >
              <PopoverTrigger asChild>
                <button
                  type="button"
                  disabled={brokersQuery.isPending}
                  aria-label="Chọn môi giới phụ trách"
                  className="flex w-full items-center gap-2.5 rounded-[10px] border border-border bg-card p-2.5 text-left transition-colors hover:bg-muted/40 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <BrokerAvatar
                    name={selectedBroker?.fullName ?? null}
                  />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-[14px] font-semibold leading-tight">
                      {selectedBroker?.fullName ?? "Chưa phân công"}
                    </span>
                    <span className="truncate font-mono text-[11px] text-muted-foreground">
                      {selectedBroker !== null
                        ? // TODO: replace with real "N leads đang theo" count
                          // once the backend exposes a broker→lead-count
                          // aggregate; running useLeads({brokerId}) per row
                          // would be wasteful.
                          "Đang phân công"
                        : "Bấm để chọn"}
                    </span>
                  </div>
                  <ChevronDown
                    aria-hidden="true"
                    className="size-3.5 shrink-0 text-muted-foreground"
                  />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-[var(--radix-popover-trigger-width)] min-w-[240px] p-0"
              >
                <Command>
                  <CommandInput placeholder="Tìm môi giới..." />
                  <CommandList>
                    <CommandEmpty>Không tìm thấy môi giới.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value="__none__"
                        onSelect={() => {
                          setValue(
                            "assignedBrokerId" as keyof LeadCreateValues,
                            "",
                            { shouldDirty: true },
                          );
                          setBrokerPickerOpen(false);
                        }}
                        className="text-muted-foreground"
                      >
                        Chưa phân công
                      </CommandItem>
                      {brokers.map((b) => (
                        <CommandItem
                          key={b.id}
                          value={b.fullName.toLowerCase()}
                          onSelect={() => {
                            setValue(
                              "assignedBrokerId" as keyof LeadCreateValues,
                              String(b.id),
                              { shouldDirty: true },
                            );
                            setBrokerPickerOpen(false);
                          }}
                        >
                          <div className="flex w-full items-center gap-2.5">
                            <BrokerAvatar name={b.fullName} small />
                            <span className="truncate text-[12px]">
                              {b.fullName}
                            </span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </section>
          ) : null}

          {/* FOLLOW-UP TIẾP THEO */}
          <section>
            <p className="mb-2 font-mono text-[11px] font-semibold tracking-[0.06em] text-ink-faint uppercase">
              Follow-up tiếp theo
            </p>
            <Select
              value={followUpPick}
              onValueChange={(v) => setFollowUpPick(v as FollowUpPick)}
            >
              <SelectTrigger className="w-full">
                <span className="flex items-center gap-2">
                  <Calendar
                    aria-hidden="true"
                    className="size-3.5 text-muted-foreground"
                  />
                  <SelectValue />
                </span>
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(FOLLOW_UP_LABELS) as FollowUpPick[]).map((k) => (
                  <SelectItem key={k} value={k}>
                    {FOLLOW_UP_LABELS[k]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {followUpPick === "custom" ? (
              <input
                type="datetime-local"
                {...register("nextFollowUpAt" as keyof LeadCreateValues)}
                className="mt-2 h-9 w-full rounded-[9px] border border-border bg-card px-2.5 text-[12px] outline-none focus:border-primary focus:ring-[3px] focus:ring-ring"
              />
            ) : null}
          </section>
        </aside>
      </div>

      {/* ============================================================== */}
      {/* Footer                                                         */}
      {/* ============================================================== */}
      <footer className="flex items-center justify-between gap-3 border-t border-border bg-muted/40 px-5 py-3">
        <p className="text-[12px] text-ink-faint">
          Trạng thái: Mới · sẽ thêm vào pipeline
        </p>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Huỷ
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <Loader2 aria-hidden="true" className="size-4 animate-spin" />
            ) : null}
            Tạo khách hàng tiềm năng
            <ArrowRight aria-hidden="true" className="size-3.5" />
          </Button>
        </div>
      </footer>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Customer sub-section (phone-lookup mode only).
//
// Renders the phone field + lookup widget + the selected-state card (when
// matched) or the editable name/email fields (when new). The "Đã có hồ
// sơ" badge in the design's matched card variant is rendered here.
// ---------------------------------------------------------------------------

interface CustomerSectionProps {
  phoneValue: string;
  customerState:
    | { kind: "existing"; customer: Customer }
    | { kind: "new" };
  onLookupStateChange: (state: CustomerLookupState) => void;
  register: ReturnType<typeof useForm<LeadCreateValues>>["register"];
  errors: ReturnType<typeof useForm<LeadCreateValues>>["formState"]["errors"];
  setValue: ReturnType<typeof useForm<LeadCreateValues>>["setValue"];
  clearErrors: ReturnType<typeof useForm<LeadCreateValues>>["clearErrors"];
}

function CustomerSection({
  phoneValue,
  customerState,
  onLookupStateChange,
  register,
  errors,
  setValue,
  clearErrors,
}: CustomerSectionProps) {
  return (
    <section>
      <p className="mb-2 text-[11px] font-semibold text-ink-soft">Khách hàng</p>

      {customerState.kind === "existing" ? (
        <ExistingCustomerCard customer={customerState.customer} />
      ) : null}

      {customerState.kind === "new" ? (
        <div className="flex flex-col gap-2">
          <div className="space-y-1.5">
            <Label
              htmlFor="phoneNumber"
              className="text-[11px] font-medium text-ink-soft"
            >
              Số điện thoại khách hàng *
            </Label>
            <Input
              id="phoneNumber"
              type="tel"
              autoComplete="tel"
              inputMode="tel"
              placeholder="0912 345 678"
              {...register("phoneNumber" as keyof LeadCreateValues)}
              aria-invalid={
                "phoneNumber" in errors && errors.phoneNumber ? "true" : "false"
              }
            />
            {"phoneNumber" in errors && errors.phoneNumber ? (
              <div role="alert" className="flex flex-col gap-1">
                <p className="text-sm text-destructive">
                  {String(errors.phoneNumber.message)}
                </p>
                {errors.phoneNumber.type === "duplicate" ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="self-start"
                    onClick={() => {
                      const current = phoneValue;
                      setValue(
                        "phoneNumber" as keyof LeadCreateValues,
                        "",
                        { shouldValidate: false },
                      );
                      window.setTimeout(() => {
                        setValue(
                          "phoneNumber" as keyof LeadCreateValues,
                          current,
                          { shouldValidate: true },
                        );
                        clearErrors("phoneNumber" as keyof LeadCreateValues);
                      }, 0);
                    }}
                  >
                    <RefreshCw aria-hidden="true" className="size-3.5" />
                    Tải lại
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>

          <CustomerPhoneLookup
            phone={phoneValue}
            onStateChange={onLookupStateChange}
          />

          {phoneValue.length > 0 ? (
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label
                  htmlFor="fullName"
                  className="text-[11px] font-medium text-ink-soft"
                >
                  Họ và tên
                </Label>
                <Input
                  id="fullName"
                  autoComplete="name"
                  maxLength={200}
                  {...register("fullName" as keyof LeadCreateValues)}
                  aria-invalid={
                    "fullName" in errors && errors.fullName ? "true" : "false"
                  }
                />
                {"fullName" in errors && errors.fullName ? (
                  <p role="alert" className="text-sm text-destructive">
                    {String(errors.fullName.message)}
                  </p>
                ) : null}
              </div>
              <div className="space-y-1.5">
                <Label
                  htmlFor="email"
                  className="text-[11px] font-medium text-ink-soft"
                >
                  Email (tùy chọn)
                </Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  {...register("email" as keyof LeadCreateValues)}
                  aria-invalid={
                    "email" in errors && errors.email ? "true" : "false"
                  }
                />
                {"email" in errors && errors.email ? (
                  <p role="alert" className="text-sm text-destructive">
                    {String(errors.email.message)}
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sub-views
// ---------------------------------------------------------------------------

function ExistingCustomerCard({ customer }: { customer: Customer }) {
  const initials = customer.fullName
    .split(/\s+/)
    .filter(Boolean)
    .map((s) => s[0])
    .slice(-2)
    .join("")
    .toUpperCase();
  return (
    <div
      role="status"
      aria-label="Khách hàng đã có"
      className="rounded-[10px] border border-border bg-card p-3"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <UserIcon aria-hidden="true" className="size-3.5 text-muted-foreground" />
          <span className="text-[14px] font-semibold leading-tight">
            {customer.fullName}
          </span>
        </div>
        <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-mono text-[11px] font-semibold text-emerald-700">
          Đã có hồ sơ
        </span>
      </div>
      <div className="mt-2 flex items-center gap-2.5">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-200 to-indigo-300 text-[11px] font-bold text-indigo-700">
          {initials || "?"}
        </div>
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-[12px] font-medium">
            {customer.fullName}
          </span>
          <span className="truncate font-mono text-[11px] text-muted-foreground">
            {[customer.phoneNumber, customer.email]
              .filter((s): s is string => Boolean(s))
              .join(" · ")}
          </span>
        </div>
      </div>
    </div>
  );
}

function LockedCustomerCard({ customer }: { customer: Customer }) {
  return (
    <section>
      <p className="mb-2 text-[11px] font-semibold text-ink-soft">Khách hàng</p>
      <ExistingCustomerCard customer={customer} />
    </section>
  );
}

function ListingTypePill({
  value,
  label,
  active,
  onClick,
}: {
  value: ListingType;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      data-listing-type={value}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-[12px] font-semibold transition-colors",
        active
          ? "border-indigo-300 bg-indigo-50 text-indigo-700"
          : "border-border bg-card text-ink-soft hover:bg-muted",
      )}
    >
      {active ? <Check aria-hidden="true" className="size-3.5" /> : null}
      {label}
    </button>
  );
}

function BrokerAvatar({
  name,
  small,
}: {
  name: string | null;
  small?: boolean;
}) {
  const initials = useMemo(() => {
    if (name === null) return "?";
    return (
      name
        .split(/\s+/)
        .filter(Boolean)
        .map((s) => s[0])
        .slice(-2)
        .join("")
        .toUpperCase() || "?"
    );
  }, [name]);
  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-200 to-indigo-300 font-bold text-indigo-700",
        small ? "size-7 text-[11px]" : "size-9 text-[11px]",
      )}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatBudgetReadout(
  [lo, hi]: [number, number],
  scale: BudgetScale,
): string {
  const unitLabel = scale.unit === "ty" ? "tỷ VND" : "triệu VND/tháng";
  const hiSuffix = hi >= scale.max ? "+" : "";
  return `${formatBudget(lo, scale.unit)} — ${formatBudget(hi, scale.unit)}${hiSuffix} ${unitLabel}`;
}
